import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import '../Styles/GalleryView.css';

const GalleryView = () => {
  const [images, setImages] = useState([]);
  const [categoriesList, setCategoriesList] = useState([{ key: 'all', label: 'All' }]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false); // ← add her
  const modalImageRef = useRef(null);
  const modalContentRef = useRef(null);

  // Fetch images and build categories dynamically
  useEffect(() => {
    fetchImagesAndCategories();
    // eslint-disable-next-line
  }, [selectedCategory]);

  const fetchImagesAndCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/gallery`);
      let allImages = [];
      let categories = [];
      res.data.forEach(gallery => {
        if (gallery.images && gallery.images.length > 0) {
          // Use gallery name as label and a slug as key
          const key = gallery.name ? gallery.name.toLowerCase().replace(/\s+/g, '_') : '';
          categories.push({
            key,
            label: gallery.name,
            updatedAt: gallery.updatedAt || '',
          });
        }
        (gallery.images || []).forEach(img => {
          allImages.push({
            ...img,
            category: gallery.name ? gallery.name.toLowerCase().replace(/\s+/g, '_') : 'all',
            galleryName: gallery.name,
          });
        });
      });
      // Sort images by id descending (newest first)
      allImages.sort((a, b) => b.id - a.id);
      // Find and remove 'Outstanding Workplaces Awards' if it exists
      const awardsKey = 'outstanding_workplaces_awards';
      let awardsCategory = null;
      const otherCategories = [];
      categories.forEach(cat => {
        if (cat.key === awardsKey) {
          awardsCategory = cat;
        } else {
          otherCategories.push(cat);
        }
      });
      // Sort the rest by updatedAt descending (newest first)
      otherCategories.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      // Build categories list: All, then Awards (if exists), then the rest
      let categoriesArr = [{ key: 'all', label: 'All' }];
      if (awardsCategory) {
        categoriesArr.push({ key: awardsCategory.key, label: awardsCategory.label });
      }
      categoriesArr = [
        ...categoriesArr,
        ...otherCategories.map(({ key, label }) => ({ key, label }))
      ];
      setCategoriesList(categoriesArr);
      if (selectedCategory === 'all') {
        setImages(allImages);
      } else {
        setImages(allImages.filter(img => img.category === selectedCategory));
      }
    } catch (err) {
      setImages([]);
      setCategoriesList([{ key: 'all', label: 'All' }]);
    }
  };

  // Modal controls
  const openModal = idx => {
    setCurrentIndex(idx);
    setModalOpen(true);
    setZoomed(false);
  };
  const closeModal = () => {
    setModalOpen(false);
    setZoomed(false);
  };
  const showPrev = e => {
    e.stopPropagation();
    setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
  };
  const showNext = e => {
    e.stopPropagation();
    setCurrentIndex(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
  };

  // Fullscreen
  const handleFullscreen = () => {
    if (modalContentRef.current) {
      if (modalContentRef.current.requestFullscreen) {
        modalContentRef.current.requestFullscreen();
      }
    }
  };

  // Download
  const handleDownload = () => {
    const img = images[currentIndex];
    const imageUrl = `${API_BASE_URL}${img.image_url}`;
    const fileName = img.image_url.split('/').pop();
    fetch(imageUrl, { mode: 'cors' })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        alert('Failed to download image.');
      });
  };

  // Zoom
  const handleZoom = () => setZoomed(z => !z);

  // Keyboard navigation
  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = e => {
      if (e.key === 'ArrowLeft') showPrev(e);
      if (e.key === 'ArrowRight') showNext(e);
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [modalOpen, currentIndex, images.length]);

  // Swipe navigation
  useEffect(() => {
    if (!modalOpen) return;
    let startX = 0;
    const handleTouchStart = e => {
      startX = e.touches[0].clientX;
    };
    const handleTouchEnd = e => {
      const endX = e.changedTouches[0].clientX;
      if (startX - endX > 50) showNext(e);
      if (endX - startX > 50) showPrev(e);
    };
    const modalBody = document.querySelector('.modal-body');
    if (modalBody) {
      modalBody.addEventListener('touchstart', handleTouchStart);
      modalBody.addEventListener('touchend', handleTouchEnd);
      return () => {
        modalBody.removeEventListener('touchstart', handleTouchStart);
        modalBody.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [modalOpen, currentIndex, images.length]);

  // Pan image when zoomed
  const handlePan = e => {
    if (!zoomed || !modalImageRef.current) return;
    const rect = modalImageRef.current.getBoundingClientRect();
    let x, y;
    if (e.type === 'mousemove') {
      x = e.clientX;
      y = e.clientY;
    } else if (e.type === 'touchmove') {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    }
    const xPercent = ((x - rect.left) / rect.width) * 100;
    const yPercent = ((y - rect.top) / rect.height) * 100;
    modalImageRef.current.style.transformOrigin = `${xPercent}% ${yPercent}%`;
  };

  // Double click/tap to zoom
  let lastTap = 0;
  const handleDoubleTap = e => {
    const now = Date.now();
    if (now - lastTap < 300) {
      handleZoom();
    }
    lastTap = now;
  };

 return (
    <div className="gallery-page">
      {/* Category Dropdown */}
      <div className="gallery-dropdown-wrapper">
        <h2 className="gallery-heading">All</h2>
        <div className="gallery-dropdown-container">
          <div className="gallery-dropdown-trigger" onClick={() => setDropdownOpen(prev => !prev)}>
            <span>{categoriesList.find(c => c.key === selectedCategory)?.label || 'All'}</span>
            <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'}`}></i>
          </div>
          {dropdownOpen && (
            <div className="gallery-dropdown-menu">
              {categoriesList.map(cat => (
                <div
                  key={cat.key}
                  className={`gallery-dropdown-item${selectedCategory === cat.key ? ' active' : ''}`}
                  onClick={() => { setSelectedCategory(cat.key); setDropdownOpen(false); }}
                >
                  {cat.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Grid */}
      <div className="gallery-grid">
        {images.length > 0 ? (
          images.map((img, idx) => (
            <div key={img.id || idx} className="gallery-item" onClick={() => openModal(idx)}>
              <img
                src={`${API_BASE_URL}${img.image_url}`}
                alt={img.galleryName}
                className="gallery-thumb"
              />
            </div>
          ))
        ) : (
          <p>No images found.</p>
        )}
      </div>

      {/* Modal */}
      {modalOpen && images.length > 0 && (
        <div className="modal" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered custom-modal-dialog m-0">
            <div className={`modal-content custom-modal-content${zoomed ? ' zoomed-in' : ''}`} ref={modalContentRef}>
              <div className="modal-header" style={{ borderBottom: 'none' }}>
                <h5 className="modal-title">
                  <span id="currentImage">{currentIndex + 1}</span> / <span id="totalImages">{images.length}</span>
                </h5>
                <div className="modal-header-buttons">
                  <button type="button" className="btn" title="Fullscreen" onClick={handleFullscreen}>
                    <i className="fas fa-expand"></i>
                  </button>
                  <button type="button" className="btn" title="Zoom" onClick={handleZoom}>
                    <i className={`fas ${zoomed ? 'fa-search-minus' : 'fa-search-plus'}`}></i>
                  </button>
                  <button type="button" className="share-button" title="Download" onClick={handleDownload}>
                    <i className="fas fa-download"></i>
                  </button>
                  <button type="button" className="close" aria-label="Close" onClick={closeModal}>
                    <span>&times;</span>
                  </button>
                </div>
              </div>
              <div
                className="modal-body text-center"
                onMouseMove={handlePan}
                onTouchMove={handlePan}
                onDoubleClick={handleZoom}
                onTouchEnd={handleDoubleTap}
              >
                <img
                  ref={modalImageRef}
                  id="modalImage"
                  src={`${API_BASE_URL}${images[currentIndex].image_url}`}
                  alt="Image"
                  className={`img-fluid${zoomed ? ' zoomed-in' : ''}`}
                  style={zoomed ? { transform: 'scale(2)', cursor: 'move' } : { transform: '', cursor: 'pointer' }}
                  onClick={handleZoom}
                />
                <button type="button" className="prev" id="prevImage" onClick={showPrev}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button type="button" className="next" id="nextImage" onClick={showNext}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              <div className="modal-footer" style={{ borderTop: 'none' }}>
                <div className="modal-caption">{images[currentIndex].galleryName}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default GalleryView;